import { CarDTO, HubSpotControllerService } from '@/openapi';
import styles from './CarForm.module.css';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import InputField from '../UI/InputField';
import ImageUploader from '../ImageUploader';
import Button from '../UI/Button';
import Label from '../UI/Label';
import { ImageSlot } from '../ImageUploader/ImageUploader';
import SuccessToast from '@/components/UI/SuccessToast';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import Loader from '../UI/Loader';

interface CarFormProps {
	onSubmit: (car: CarDTO, carImages: ImageSlot[]) => void;
	editingCar?: CarDTO | null;
	isEditing?: boolean;
	existingImages?: ImageSlot[];
	onCancelEdit?: () => void; // Callback per gestire l'uscita dalla modalità modifica
}

const initialCarData: CarDTO = {
	category: 'RENTAL',
	dateCreate: new Date().toISOString(),
	dateUpdate: new Date().toISOString(),
	imageIds: []
};

const CarForm = (props: CarFormProps) => {
	const { t } = useTranslation();

	const authCtx = useContext(AuthContext);
	const isRoleAdmin = authCtx.role?.some((r) => r.name === 'ROLE_ADMIN');

	// Stati per i toast di errore
	const [showToast, setShowToast] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState<string>('');

	const [images, setImages] = useState<ImageSlot[]>([]);
	const [uploaderKey, setUploaderKey] = useState(0);

	const allowedCategoryValues = ['RENTAL', 'USED' /*, 'VARIOUS'*/] as const;
	const [category, setCategory] = useState('RENTAL');

	const [carData, setCarData] = useState<CarDTO>(initialCarData);

	const getBrandOptions = useRemoteGet<Record<string, Record<string, any>>>({
		remoteMethod: async () => {
			return HubSpotControllerService.getDealPropertyOptions({ propertyName: 'brand' });
		},
		lazy: true
	});

	useEffect(() => {
		// Carica le opzioni del brand
		getBrandOptions.fetch();
	}, []);

	// Se in modalità modifica carica i dati iniziali
	useEffect(() => {
		if (props.isEditing && props.editingCar) {
			setCarData(props.editingCar);
			setCategory(props.editingCar.category || 'RENTAL');
		}
	}, [props.isEditing, props.editingCar]);

	// funzione di reset
	const resetForm = () => {
		setCarData(initialCarData);
		setImages([]);
		setCategory(initialCarData.category!);

		// Notifica al genitore che sta uscendo dalla modalità modifica
		if (props.isEditing) {
			// Se è in modalità modifica, comunica al componente Admin che ha terminato
			if (props.onCancelEdit) {
				props.onCancelEdit();
			}
		}

		// cambio la key: ImageUploader si smonta e rimonta “vuoto”
		setUploaderKey((k) => k + 1);
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Validazione prima dell'invio dei dati
		if (
			!carData.brand ||
			!carData.model ||
			(category === 'RENTAL' && !carData.pricePerMonth) ||
			(category === 'USED' && !carData.totalPrice) ||
			!carData.agentCommission
		) {
			setShowToast(true);
			setToastMessage(t('adminPage.offersSection.error'));
			return;
		}

		if (!isRoleAdmin) {
			setShowToast(true);
			setToastMessage(t('adminPage.accessDenied'));
			return;
		}

		try {
			await props.onSubmit(carData, images);
			resetForm();
		} catch (error: any) {
			setShowToast(true);
			setToastMessage(error.message);
		}
	};

	const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
		const { name, value, type } = e.target;

		if (type === 'checkbox') {
			const { checked } = e.target as HTMLInputElement;
			setCarData((prev) => ({
				...prev,
				[name]: checked
			}));
			return;
		}

		let newValue: any = value;

		// Gestione speciale per averageConsumption (decimale con max 2 cifre)
		if (name === 'averageConsumption') {
			if (value === '') newValue = undefined;
			else {
				const numValue = parseFloat(value);
				if (!isNaN(numValue) && numValue >= 0) newValue = Math.round(numValue * 100) / 100;
				else if (numValue < 0) newValue = 0;
			}
		}

		// Se il campo è di tipo number o ha un nome specifico, converte il valore in Number
		if (
			type === 'number' ||
			[
				'pricePerMonth',
				'totalPrice',
				'km',
				'months',
				'agentCommission',
				'advance',
				'averageConsumption',
				'horsepower',
				'co2Emissions',
				'engineDisplacement',
				'doors',
				'productionYear'
			].includes(name)
		) {
			if (value === '') {
				newValue = undefined;
			} else {
				const numValue = Number(value);
				newValue = numValue < 0 ? 0 : numValue;
			}
		}

		setCarData((prev) => ({
			...prev,
			[name]: newValue
		}));
	};

	const changeCategory = (newCategory: string) => {
		if (allowedCategoryValues.includes(newCategory as any)) {
			setCategory(newCategory);
			setCarData((prev) => ({
				...prev,
				category: newCategory as CarDTO['category']
			}));
		}
	};

	return (
		<form onSubmit={handleSubmit} className={styles.carForm} id="carForm">
			<Loader visible={getBrandOptions.loading} />

			<ImageUploader key={uploaderKey} onFilesChange={setImages} existingImages={props.existingImages} />

			<SuccessToast isVisible={showToast} message={toastMessage} type={'error'} onClose={() => setShowToast(false)} />

			<div className={styles.category_container}>
				<Label text={t('carDetails.category.category')} className={styles.category_label} />
				<div className={styles.category_btn_container}>
					<Button
						className={`${styles.category_btn} ${category === 'RENTAL' ? styles.selected : ''}`}
						typeSubmit="button"
						onClick={() => {
							changeCategory('RENTAL');
						}}
						name={t('carDetails.category.rental')}
						color="black"
						background="white"
					/>
					<Button
						className={`${styles.category_btn} ${category === 'USED' ? styles.selected : ''}`}
						typeSubmit="button"
						onClick={() => {
							changeCategory('USED');
						}}
						name={t('carDetails.category.used')}
						color="black"
						background="white"
					/>
					{/* 
					<Button
						className={`${styles.category_btn} ${category === 'VARIOUS' ? styles.selected : ''}`}
						typeSubmit="button"
						onClick={() => {
							changeCategory('VARIOUS');
						}}
						name={t('carDetails.category.various')}
						color="black"
						background="white"
					/>
					*/}
				</div>
			</div>

			<div className={styles.gridContainer}>
				<div className={styles.selectContainer}>
					<Label text={t('carDetails.brand')} className={styles.brandLabel} />

					<select
						name="brand"
						value={carData.brand || ''}
						onChange={handleInputChange}
						required
						className={styles.filterSelect}
					>
						<option value="">{t('carDetails.brand')}</option>
						{getBrandOptions.data?.options?.map((option: Record<string, any>) => (
							<option key={option.value} value={option.value.charAt(0).toUpperCase() + option.value.slice(1).toLowerCase()}>
								{option.label.charAt(0).toUpperCase() + option.label.slice(1).toLowerCase()}
							</option>
						))}
					</select>
				</div>
				<InputField
					name="model"
					value={carData.model || ''}
					type="text"
					title={t('carDetails.model')}
					required
					label={t('carDetails.model')}
					placeholder={t('carDetails.model')}
					onChange={handleInputChange}
					error={showToast}
				/>
				<InputField
					name="productionYear"
					value={carData.productionYear ?? ''}
					type="number"
					title={t('carDetails.productionYear')}
					required={false}
					label={t('carDetails.productionYear')}
					placeholder={t('carDetails.productionYear')}
					onChange={handleInputChange}
				/>
				<InputField
					name="advance"
					value={carData.advance ?? ''}
					type="number"
					title={t('carDetails.advance')}
					required={false}
					label={t('carDetails.advance')}
					placeholder={t('carDetails.advance') + ' ' + t('carDetailsUnitOfMeasurement.price')}
					onChange={handleInputChange}
				/>
				<InputField
					name="pricePerMonth"
					value={carData.pricePerMonth ?? ''}
					type="number"
					title={t('carDetails.pricePerMonth')}
					required={category === 'RENTAL' ? true : false}
					label={t('carDetails.pricePerMonth')}
					placeholder={t('carDetails.pricePerMonth') + ' ' + t('carDetailsUnitOfMeasurement.price')}
					onChange={handleInputChange}
					error={category === 'RENTAL' ? showToast : undefined}
				/>
				<InputField
					name="totalPrice"
					value={carData.totalPrice ?? ''}
					type="number"
					title={t('carDetails.totalPrice')}
					required={category === 'USED' ? true : false}
					label={t('carDetails.totalPrice')}
					placeholder={t('carDetails.totalPrice') + ' ' + t('carDetailsUnitOfMeasurement.price')}
					onChange={handleInputChange}
					error={category === 'USED' ? showToast : undefined}
				/>
				<InputField
					name="months"
					value={carData.months ?? ''}
					type="number"
					title={t('carDetails.months')}
					required={category === 'RENTAL' ? true : false}
					label={t('carDetails.months')}
					placeholder={t('carDetails.months')}
					onChange={handleInputChange}
					error={category === 'RENTAL' ? showToast : undefined}
				/>
				<InputField
					name="agentCommission"
					value={carData.agentCommission ?? ''}
					type="number"
					title={t('carDetails.agentCommission')}
					required
					label={t('carDetails.agentCommission')}
					placeholder={t('carDetails.agentCommission')}
					onChange={handleInputChange}
					error={showToast}
				/>
				<InputField
					name="km"
					value={carData.km ?? ''}
					type="number"
					title={t('carDetails.km')}
					required={false}
					label={t('carDetails.km')}
					placeholder={t('carDetails.km')}
					onChange={handleInputChange}
				/>
				<InputField
					name="horsepower"
					value={carData.horsepower ?? ''}
					type="number"
					title={t('carDetails.horsepower')}
					required={false}
					label={t('carDetails.horsepower')}
					placeholder={t('carDetails.horsepower') + ' ' + t('carDetailsUnitOfMeasurement.horsepower')}
					onChange={handleInputChange}
				/>
				<InputField
					name="engineDisplacement"
					value={carData.engineDisplacement ?? ''}
					type="number"
					title={t('carDetails.engineDisplacement')}
					required={false}
					label={t('carDetails.engineDisplacement')}
					placeholder={t('carDetails.engineDisplacement') + ' ' + t('carDetailsUnitOfMeasurement.engineDisplacement')}
					onChange={handleInputChange}
				/>
				<InputField
					name="averageConsumption"
					value={carData.averageConsumption ?? ''}
					type="number"
					step="0.01"
					title={t('carDetails.averageConsumption')}
					required={false}
					label={t('carDetails.averageConsumption')}
					placeholder={t('carDetails.averageConsumption') + ' ' + t('carDetailsUnitOfMeasurement.averageConsumption')}
					onChange={handleInputChange}
				/>
				<InputField
					name="co2Emissions"
					value={carData.co2Emissions ?? ''}
					type="number"
					title={t('carDetails.co2Emissions')}
					required={false}
					label={t('carDetails.co2Emissions')}
					placeholder={t('carDetails.co2Emissions') + ' ' + t('carDetailsUnitOfMeasurement.co2Emissions')}
					onChange={handleInputChange}
				/>
				<InputField
					name="fuelType"
					value={carData.fuelType || ''}
					type="text"
					title={t('carDetails.fuelType')}
					required={false}
					label={t('carDetails.fuelType')}
					placeholder={t('carDetails.fuelType')}
					onChange={handleInputChange}
				/>
				<InputField
					name="driveType"
					value={carData.driveType || ''}
					type="text"
					title={t('carDetails.driveType')}
					required={false}
					label={t('carDetails.driveType')}
					placeholder={t('carDetails.driveType')}
					onChange={handleInputChange}
				/>
				<InputField
					name="transmission"
					value={carData.transmission || ''}
					type="text"
					title={t('carDetails.transmission')}
					required={false}
					label={t('carDetails.transmission')}
					placeholder={t('carDetails.transmission')}
					onChange={handleInputChange}
				/>
				<InputField
					name="doors"
					value={carData.doors ?? ''}
					type="number"
					title={t('carDetails.doors')}
					required={false}
					label={t('carDetails.doors')}
					placeholder={t('carDetails.doors')}
					onChange={handleInputChange}
				/>
			</div>

			<div className={styles.servicesContainer}>
				<Label text={t('carDetails.services.title')} className={styles.servicesTitle} />
				<div className={styles.checkboxGrid}>
					{['fullCoverage', 'liabilityInsurance', 'fireTheft', 'driverInjury', 'maintenance', 'roadsideAssistance'].map(
						(serviceName) => (
							<div key={serviceName} className={styles.checkboxWrapper}>
								<input
									type="checkbox"
									id={serviceName}
									name={serviceName}
									checked={!!carData[serviceName as keyof CarDTO]}
									onChange={handleInputChange}
									className={styles.checkboxInput}
								/>
								<label htmlFor={serviceName} className={styles.checkboxLabel}>
									{t(`carDetails.services.${serviceName}`)}
								</label>
							</div>
						)
					)}
				</div>
			</div>

			<div className={styles.box_btn}>
				<div className={styles.btns}>
					<Button
						className={styles.btn}
						typeSubmit="submit"
						onClick={() => {}}
						name={props.isEditing ? t('adminPage.offersSection.editBtn') : t('adminPage.offersSection.save')}
						color="black"
						background="white"
					/>
					<Button
						className={styles.btn}
						typeSubmit="button"
						onClick={() => resetForm()}
						name={t('common.cancel')}
						color="black"
						background="white"
					/>
				</div>
			</div>
		</form>
	);
};

export default CarForm;
